import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { card, input, label, btnPrimary, btnGhost, h1 } from "./ui";
import { Plus, Trash2, Upload, Save, Loader2, Sparkles, History, Monitor, Smartphone, X } from "lucide-react";

const ICONS = ["Waves", "Heart", "Users", "Flame", "Compass", "CloudRain", "Sparkles", "Brain", "Leaf"];

const TABS = [
  { id: "inicio", label: "Início" },
  { id: "sobre", label: "Sobre" },
  { id: "especialidades", label: "Especialidades" },
  { id: "abordagem", label: "Abordagem" },
  { id: "horarios", label: "Horários" },
  { id: "contato", label: "Contato" },
];

const AI_MODES = [
  { id: "melhorar", label: "Melhorar redação" },
  { id: "resumir", label: "Resumir" },
  { id: "corrigir", label: "Corrigir ortografia" },
  { id: "tcc", label: "Adaptar à TCC" },
];

const MARK_HINT = "Use *asteriscos* no trecho que aparece em fonte manuscrita.";
const CONTENT_FIELDS = [
  "about_title", "about_text", "email", "instagram", "whatsapp", "whatsapp_msg", "logo_hide_name",
  "hero_kicker", "hero_title", "hero_script", "hero_subtitle", "hero_tagline",
  "services_title", "services_intro",
  "approach_kicker", "approach_title", "approach_intro",
  "availability_title", "availability_intro",
  "contact_title", "contact_intro",
];

export default function SiteContent() {
  const [c, setC] = useState(null);
  const [legal, setLegal] = useState(null);
  const [tab, setTab] = useState("inicio");
  const [saving, setSaving] = useState(false);
  const [savingLegal, setSavingLegal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [waAiLoading, setWaAiLoading] = useState(false);
  const [aiBox, setAiBox] = useState(null); // { k, lbl }
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSug, setAiSug] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVp, setPreviewVp] = useState("desktop");
  const [histOpen, setHistOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);
  const logoRef = useRef(null);

  const load = () => api.get("/content").then(({ data }) => setC(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/legal").then(({ data }) => setLegal(data))
      .catch(() => setLegal({ privacy_body: "", terms_body: "", updated_at: "", legal_email: "" }));
  }, []);

  if (!c) return <Loader2 className="w-6 h-6 animate-spin text-[#5B6E5B]" />;

  const set = (k, v) => setC({ ...c, [k]: v });
  const setSpec = (i, k, v) => { const s = [...c.specialties]; s[i] = { ...s[i], [k]: v }; setC({ ...c, specialties: s }); };
  const addSpec = () => setC({ ...c, specialties: [...c.specialties, { title: "", desc: "", icon: "Sparkles" }] });
  const delSpec = (i) => setC({ ...c, specialties: c.specialties.filter((_, idx) => idx !== i) });
  const setApproachCard = (i, k, v) => {
    const cards = [...(c.approach_cards || [])];
    cards[i] = { ...cards[i], [k]: v };
    setC({ ...c, approach_cards: cards });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      CONTENT_FIELDS.forEach((k) => { payload[k] = c[k]; });
      payload.specialties = c.specialties;
      payload.approach_cards = c.approach_cards;
      await api.put("/content", payload);
      toast.success("Conteúdo publicado! O site já está atualizado, sem precisar de deploy. 🌿");
    } catch { toast.error("Erro ao publicar."); }
    setSaving(false);
  };

  const saveLegal = async () => {
    setSavingLegal(true);
    try {
      await api.put("/legal", {
        privacy_body: legal.privacy_body, terms_body: legal.terms_body,
        updated_at: legal.updated_at || "", legal_email: legal.legal_email || "",
      });
      toast.success("Textos legais atualizados! As páginas públicas já usam o novo conteúdo.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar textos legais."); }
    setSavingLegal(false);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC({ ...c, about_photo_path: data.about_photo_path });
      toast.success("Foto atualizada!");
    } catch { toast.error("Erro ao enviar foto."); }
    setUploading(false);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/content/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setC({ ...c, logo_path: data.logo_path });
      toast.success("Logo atualizada! 🌿");
    } catch { toast.error("Erro ao enviar a logo."); }
    setUploadingLogo(false);
  };

  const removeLogo = async () => {
    setUploadingLogo(true);
    try {
      await api.delete("/content/logo");
      setC({ ...c, logo_path: null });
      toast.success("Logo removida. Voltamos ao emblema padrão.");
    } catch { toast.error("Erro ao remover a logo."); }
    setUploadingLogo(false);
  };

  const genWaMsg = async () => {
    setWaAiLoading(true);
    try {
      const { data } = await api.post("/content/whatsapp-message");
      set("whatsapp_msg", data.message);
      toast.success("Mensagem gerada! Revise e salve as alterações.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui gerar agora. Tente de novo.");
    } finally { setWaAiLoading(false); }
  };

  // ---- Assistente de redação (IA só sugere; aplicar é sempre manual) ----
  const openAi = (k, lbl) => { setAiBox({ k, lbl }); setAiSug(""); };
  const suggest = async (mode) => {
    if (!aiBox) return;
    const current = (c[aiBox.k] || "").trim();
    if (current.length < 2) return toast.error("Escreva algo no campo antes de pedir uma sugestão.");
    setAiBusy(true);
    setAiSug("");
    try {
      const { data } = await api.post("/content/ai-suggest", { field_label: aiBox.lbl, text: current, mode });
      setAiSug(data.suggestion);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistente de redação indisponível no momento.");
    } finally { setAiBusy(false); }
  };
  const applyAi = () => {
    if (!aiBox || !aiSug) return;
    set(aiBox.k, aiSug);
    toast.success("Sugestão aplicada no campo. Revise e clique em Publicar.");
    setAiBox(null); setAiSug("");
  };

  // ---- Pré-visualização (rascunho fica só no navegador da administradora) ----
  const openPreview = (vp) => {
    localStorage.setItem("content_preview", JSON.stringify(c));
    setPreviewVp(vp);
    setPreviewOpen(true);
  };

  // ---- Histórico de versões ----
  const openHistory = async () => {
    try {
      const { data } = await api.get("/content/versions");
      setVersions(data);
      setConfirmId(null);
      setHistOpen(true);
    } catch { toast.error("Não consegui carregar o histórico."); }
  };
  const restore = async (id) => {
    try {
      const { data } = await api.post(`/content/restore/${id}`);
      setC(data);
      setHistOpen(false);
      setConfirmId(null);
      toast.success("Versão restaurada e publicada no site.");
    } catch { toast.error("Não consegui restaurar esta versão."); }
  };

  const Field = ({ k, lbl, hint, textarea, rows = 3 }) => (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className={label}>{lbl}</label>
        <button type="button" onClick={() => openAi(k, lbl)} data-testid={`ai-btn-${k}`} title="Sugerir melhoria com IA"
          className="inline-flex items-center gap-1 font-body text-[11px] text-[#C87A5E] hover:text-[#B2644D] transition-colors">
          <Sparkles className="w-3.5 h-3.5" /> IA
        </button>
      </div>
      {textarea ? (
        <textarea rows={rows} className={`${input} resize-y`} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`ct-${k}`} />
      ) : (
        <input className={input} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`ct-${k}`} />
      )}
      {hint && <p className="font-body text-xs text-[#7A8A7A] mt-1.5">{hint}</p>}
    </div>
  );

  const vpWidth = previewVp === "mobile" ? 390 : 1280;
  const vpScale = Math.min(1, (window.innerWidth - 48) / vpWidth);

  return (
    <div data-testid="site-content-page" className="max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Conteúdo</p>
          <h1 className={h1}>Editor do site</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className={btnGhost} onClick={openHistory} data-testid="history-open-btn">
            <History className="w-4 h-4" /> Histórico
          </button>
          <button className={btnGhost} onClick={() => openPreview("desktop")} data-testid="preview-open-btn">
            <Monitor className="w-4 h-4" /> Pré-visualizar
          </button>
          <button className={btnPrimary} onClick={save} disabled={saving} data-testid="save-content">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publicar
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="content-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`content-tab-${t.id}`}
            className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-body transition-colors ${tab === t.id ? "bg-[#5B6E5B] text-[#FDFBF7]" : "bg-white border border-[#5B6E5B]/20 text-[#4A5D4E] hover:bg-[#F5F0E6]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={`${card} p-6 sm:p-8 mt-5 space-y-5`} data-testid={`tab-panel-${tab}`}>
        {tab === "inicio" && (
          <>
            <h2 className="font-display text-2xl text-[#2C332A]">Seção Início (primeira tela)</h2>
            <Field k="hero_kicker" lbl="Texto pequeno do topo" />
            <Field k="hero_title" lbl="Título principal" textarea rows={2} hint="Uma linha por verso do título." />
            <Field k="hero_script" lbl="Trecho final em fonte manuscrita" hint='Ex.: "você florescer".' />
            <Field k="hero_subtitle" lbl="Parágrafo abaixo do título" textarea rows={4} />
            <Field k="hero_tagline" lbl="Frase manuscrita de destaque (terracota)" />
          </>
        )}

        {tab === "sobre" && (
          <>
            <h2 className="font-display text-2xl text-[#2C332A]">Seção Sobre</h2>
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-2xl bg-[#F5F0E6] overflow-hidden grid place-items-center shrink-0">
                {c.about_photo_path ? <img src={`${API}/files/${c.about_photo_path}`} alt="foto" className="w-full h-full object-cover" /> : <span className="text-xs text-[#7A8A7A]">sem foto</span>}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="photo-input" />
                <button className={btnGhost} onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photo">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Enviar foto
                </button>
                <p className="font-body text-xs text-[#7A8A7A] mt-2">JPG ou PNG. Aparece na seção &quot;Sobre&quot;.</p>
              </div>
            </div>
            <Field k="about_title" lbl="Título" hint="A última palavra aparece em fonte manuscrita." />
            <Field k="about_text" lbl="Texto (linha em branco separa parágrafos)" textarea rows={6} />
          </>
        )}

        {tab === "especialidades" && (
          <>
            <h2 className="font-display text-2xl text-[#2C332A]">Seção Especialidades</h2>
            <Field k="services_title" lbl="Título da seção" hint={MARK_HINT} />
            <Field k="services_intro" lbl="Texto abaixo do título" textarea rows={3} />
            <div className="flex items-center justify-between pt-2">
              <p className="font-body text-sm font-medium text-[#4A5D4E]">Cards</p>
              <button className={btnGhost} onClick={addSpec} data-testid="add-specialty"><Plus className="w-4 h-4" /> Adicionar</button>
            </div>
            <div className="space-y-4">
              {c.specialties.map((s, i) => (
                <div key={i} className="grid sm:grid-cols-[1fr_1.6fr_auto_auto] gap-3 items-end" data-testid="specialty-row">
                  <div><label className={label}>Título</label><input className={input} value={s.title} onChange={(e) => setSpec(i, "title", e.target.value)} data-testid="specialty-title" /></div>
                  <div><label className={label}>Descrição</label><input className={input} value={s.desc} onChange={(e) => setSpec(i, "desc", e.target.value)} data-testid="specialty-desc" /></div>
                  <div><label className={label}>Ícone</label>
                    <select className={input} value={s.icon} onChange={(e) => setSpec(i, "icon", e.target.value)} data-testid="specialty-icon">
                      {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </div>
                  <button onClick={() => delSpec(i)} className="text-[#C87A5E] hover:text-[#B2644D] pb-3" data-testid="delete-specialty"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "abordagem" && (
          <>
            <h2 className="font-display text-2xl text-[#2C332A]">Seção Abordagem (TCC)</h2>
            <Field k="approach_kicker" lbl="Texto pequeno do topo" />
            <Field k="approach_title" lbl="Título da seção" hint={MARK_HINT} />
            <Field k="approach_intro" lbl="Texto introdutório" textarea rows={3} />
            <p className="font-body text-sm font-medium text-[#4A5D4E] pt-2">Cards (numeração 01–03 automática)</p>
            <div className="space-y-4">
              {(c.approach_cards || []).map((cd, i) => (
                <div key={i} className="rounded-xl border border-[#5B6E5B]/15 p-4 space-y-3" data-testid={`approach-card-${i}`}>
                  <p className="font-display text-lg text-[#5B6E5B]">{String(i + 1).padStart(2, "0")}</p>
                  <div><label className={label}>Título</label><input className={input} value={cd.title} onChange={(e) => setApproachCard(i, "title", e.target.value)} data-testid={`approach-card-title-${i}`} /></div>
                  <div><label className={label}>Texto</label><textarea rows={2} className={`${input} resize-y`} value={cd.text} onChange={(e) => setApproachCard(i, "text", e.target.value)} data-testid={`approach-card-text-${i}`} /></div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "horarios" && (
          <>
            <h2 className="font-display text-2xl text-[#2C332A]">Seção Horários</h2>
            <Field k="availability_title" lbl="Título da seção" hint={MARK_HINT} />
            <Field k="availability_intro" lbl="Texto abaixo do título" textarea rows={3} />
            <p className="font-body text-xs text-[#7A8A7A]">Os horários exibidos vêm da aba &quot;Horários disponíveis&quot; do painel.</p>
          </>
        )}

        {tab === "contato" && (
          <>
            <h2 className="font-display text-2xl text-[#2C332A]">Seção Contato</h2>
            <Field k="contact_title" lbl="Título da seção" hint={MARK_HINT} />
            <Field k="contact_intro" lbl="Texto abaixo do título" textarea rows={3} />
            <div className="grid sm:grid-cols-3 gap-4 pt-2">
              <div><label className={label}>E-mail</label><input className={input} value={c.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="content-email" /></div>
              <div><label className={label}>Instagram</label><input className={input} value={c.instagram || ""} onChange={(e) => set("instagram", e.target.value)} data-testid="content-instagram" /></div>
              <div><label className={label}>WhatsApp que recebe as mensagens</label><input className={input} value={c.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} placeholder="55 11 90000-0000" data-testid="content-whatsapp" /></div>
            </div>
            <p className="font-body text-xs text-[#7A8A7A] -mt-2">Com o número salvo, o site mostra botões de WhatsApp (flutuante, início, contato e rodapé) para o paciente te chamar direto.</p>
            <div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className={label}>Mensagem pronta do botão de WhatsApp</label>
                <button type="button" onClick={genWaMsg} disabled={waAiLoading} data-testid="whatsapp-ai-generate"
                  className="inline-flex items-center gap-2 rounded-full border border-[#C87A5E]/35 px-4 py-1.5 text-xs text-[#C87A5E] hover:bg-[#C87A5E]/10 transition-colors disabled:opacity-60">
                  {waAiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Gerar com IA
                </button>
              </div>
              <textarea rows={2} className={`${input} resize-none`} value={c.whatsapp_msg || ""} onChange={(e) => set("whatsapp_msg", e.target.value)}
                placeholder="Olá, Tacilaine! Vim pelo seu site e gostaria de conversar sobre terapia." data-testid="content-whatsapp-msg" />
              <p className="font-body text-xs text-[#7A8A7A] mt-1.5">É o texto que já vai escrito na conversa quando o paciente toca no botão. Deixe vazio para usar a mensagem padrão.</p>
            </div>
          </>
        )}

        {aiBox && (
          <div className="rounded-2xl border border-[#C87A5E]/25 bg-[#FDFBF7] p-5 space-y-4" data-testid="ai-suggest-box">
            <p className="font-body text-sm text-[#2C332A]">Assistente de redação — <strong>{aiBox.lbl}</strong></p>
            <div className="flex flex-wrap gap-2">
              {AI_MODES.map((m) => (
                <button key={m.id} onClick={() => suggest(m.id)} disabled={aiBusy} data-testid={`ai-mode-${m.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#5B6E5B]/25 bg-white px-4 py-2 text-xs font-body text-[#4A5D4E] hover:bg-[#5B6E5B]/10 transition-colors disabled:opacity-60">
                  {m.label}
                </button>
              ))}
            </div>
            {aiBusy && <div className="flex items-center gap-2 font-body text-sm text-[#7A8A7A]"><Loader2 className="w-4 h-4 animate-spin" /> Escrevendo sugestão...</div>}
            {aiSug && (
              <>
                <div className="rounded-xl bg-white border border-[#5B6E5B]/15 p-4 font-body text-sm text-[#2C332A] whitespace-pre-wrap" data-testid="ai-suggestion">{aiSug}</div>
                <div className="flex flex-wrap gap-2">
                  <button className={btnPrimary} onClick={applyAi} data-testid="ai-apply">Usar este texto</button>
                  <button className={btnGhost} onClick={() => setAiSug("")} data-testid="ai-discard">Descartar</button>
                </div>
              </>
            )}
            <button onClick={() => { setAiBox(null); setAiSug(""); }} className="font-body text-xs text-[#7A8A7A] hover:text-[#4A5D4E]" data-testid="ai-close">Fechar assistente</button>
          </div>
        )}
      </div>

      <div className={`${card} p-6 sm:p-8 mt-6 space-y-5`}>
        <h2 className="font-display text-2xl text-[#2C332A]">Logo</h2>
        <div className="flex items-center gap-5">
          <div className="w-24 h-24 rounded-2xl bg-[#F5F0E6] overflow-hidden grid place-items-center shrink-0 p-2">
            {c.logo_path ? <img src={`${API}/files/${c.logo_path}`} alt="logo" className="w-full h-full object-contain" data-testid="logo-preview" /> : <span className="text-xs text-[#7A8A7A] text-center px-2">emblema padrão</span>}
          </div>
          <div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-input" />
            <div className="flex flex-wrap gap-2">
              <button className={btnGhost} onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo">
                {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Enviar logo
              </button>
              {c.logo_path && (
                <button className="inline-flex items-center gap-2 text-sm text-[#C87A5E] hover:text-[#B2644D]" onClick={removeLogo} disabled={uploadingLogo} data-testid="remove-logo">
                  <Trash2 className="w-4 h-4" /> Remover
                </button>
              )}
            </div>
            <p className="font-body text-xs text-[#7A8A7A] mt-2">PNG com fundo transparente fica melhor. Aparece no topo e no rodapé do site.</p>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input type="checkbox" checked={!!c.logo_hide_name} onChange={(e) => set("logo_hide_name", e.target.checked)} className="w-4 h-4 accent-[#5B6E5B]" data-testid="logo-hide-name" />
          <span className="font-body text-sm text-[#4A5D4E]">Minha logo já inclui o nome (ocultar o texto &quot;Tacilaine Almeida&quot; ao lado)</span>
        </label>
      </div>

      {legal && (
        <div className={`${card} p-6 sm:p-8 mt-6 space-y-5`} data-testid="legal-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-2xl text-[#2C332A]">Institucional e Legal</h2>
            <button className={btnGhost} onClick={saveLegal} disabled={savingLegal} data-testid="legal-save-btn">
              {savingLegal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar textos legais
            </button>
          </div>
          <p className="font-body text-xs text-[#7A8A7A] -mt-2">
            Textos exibidos nas páginas públicas /politica-de-privacidade e /termos-de-uso. Aceitam Markdown simples (## para títulos de seção, **negrito**, listas com -).
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={label}>Data de atualização</label>
              <input className={input} value={legal.updated_at || ""} onChange={(e) => setLegal({ ...legal, updated_at: e.target.value })}
                placeholder="julho de 2026" data-testid="legal-updated-input" />
            </div>
            <div>
              <label className={label}>E-mail institucional / LGPD</label>
              <input className={input} value={legal.legal_email || ""} onChange={(e) => setLegal({ ...legal, legal_email: e.target.value })}
                placeholder="contato@tacilaine.com.br" data-testid="legal-email-input" />
            </div>
          </div>
          <div>
            <label className={label}>Política de Privacidade</label>
            <textarea rows={14} className={`${input} resize-y font-mono text-xs`} value={legal.privacy_body || ""}
              onChange={(e) => setLegal({ ...legal, privacy_body: e.target.value })} data-testid="legal-privacy-input" />
          </div>
          <div>
            <label className={label}>Termos de Uso</label>
            <textarea rows={14} className={`${input} resize-y font-mono text-xs`} value={legal.terms_body || ""}
              onChange={(e) => setLegal({ ...legal, terms_body: e.target.value })} data-testid="legal-terms-input" />
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-[80] bg-[#2C332A]/95 flex flex-col" data-testid="content-preview-modal">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#FDFBF7]/10">
            <p className="font-body text-sm text-[#FDFBF7]">Pré-visualização do rascunho <span className="text-[#C7CFC2]">(não publicado)</span></p>
            <div className="flex items-center gap-2">
              <button onClick={() => openPreview("desktop")} data-testid="preview-desktop"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-body transition-colors ${previewVp === "desktop" ? "bg-[#FDFBF7] text-[#2C332A]" : "text-[#C7CFC2] hover:text-[#FDFBF7]"}`}>
                <Monitor className="w-3.5 h-3.5" /> Computador
              </button>
              <button onClick={() => openPreview("mobile")} data-testid="preview-mobile"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-body transition-colors ${previewVp === "mobile" ? "bg-[#FDFBF7] text-[#2C332A]" : "text-[#C7CFC2] hover:text-[#FDFBF7]"}`}>
                <Smartphone className="w-3.5 h-3.5" /> Celular
              </button>
              <button onClick={() => setPreviewOpen(false)} data-testid="preview-close"
                className="grid place-items-center w-8 h-8 rounded-full text-[#C7CFC2] hover:text-[#FDFBF7] hover:bg-white/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden pt-6 grid justify-items-center">
            <iframe key={previewVp} src="/?preview=1" title="Pré-visualização do site" data-testid="content-preview-frame"
              style={{ width: vpWidth, height: "92vh", transform: `scale(${vpScale})`, transformOrigin: "top center" }}
              className="bg-white rounded-t-xl shadow-2xl border-0" />
          </div>
        </div>
      )}

      {histOpen && (
        <div className="fixed inset-0 z-[80] bg-[#2C332A]/60 backdrop-blur-sm grid place-items-center px-4" data-testid="history-modal">
          <div className={`${card} w-full max-w-lg p-6 sm:p-8 max-h-[80vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl text-[#2C332A]">Histórico de publicações</h2>
              <button onClick={() => setHistOpen(false)} data-testid="history-close" className="grid place-items-center w-8 h-8 rounded-full text-[#7A8A7A] hover:bg-[#F5F0E6]"><X className="w-4 h-4" /></button>
            </div>
            {versions.length === 0 ? (
              <p className="font-body text-sm text-[#7A8A7A]">Nenhuma versão anterior ainda. O histórico começa a registrar na próxima publicação.</p>
            ) : (
              <div className="space-y-3">
                {versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#5B6E5B]/15 px-4 py-3" data-testid={`history-item-${v.id}`}>
                    <div>
                      <p className="font-body text-sm text-[#2C332A]">{new Date(v.created_at).toLocaleString("pt-BR")}</p>
                      <p className="font-body text-xs text-[#7A8A7A]">{v.note || "publicação"}</p>
                    </div>
                    {confirmId === v.id ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => restore(v.id)} data-testid={`history-confirm-${v.id}`}
                          className="rounded-full bg-[#C87A5E] px-4 py-1.5 text-xs font-body text-white hover:bg-[#B2644D] transition-colors">Confirmar</button>
                        <button onClick={() => setConfirmId(null)} className="font-body text-xs text-[#7A8A7A]">Voltar</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmId(v.id)} data-testid={`history-restore-${v.id}`}
                        className="rounded-full border border-[#5B6E5B]/25 px-4 py-1.5 text-xs font-body text-[#5B6E5B] hover:bg-[#5B6E5B]/10 transition-colors">Restaurar</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="font-body text-xs text-[#7A8A7A] mt-5">Restaurar substitui apenas os textos e cards do site público. WhatsApp, logo, integrações e textos legais são preservados se não mudaram naquela versão.</p>
          </div>
        </div>
      )}
    </div>
  );
}
